/**
 * Page Header component for SnapHub PWA.
 *
 * Renders a header bar inside the main content area with:
 * - Left: Hamburger menu button (mobile only) + dynamic page title (h2)
 * - Right: User menu (avatar dropdown with "Mein Bereich" + "Abmelden")
 *         or "Anmelden" link when not authenticated
 *
 * The hamburger button is integrated into the header so it stays visible
 * in PWA standalone mode (where a fixed-positioned button would be hidden
 * behind the native title bar).
 *
 * Validates: Requirements 37.1, 37.2, 38.1, 38.2
 */

import { getTitle } from '../services/page-title.service';
import { toggleMobileSidebar } from './sidebar-navigation';
import { isAuthenticated } from '../../infrastructure/auth-guard';
import { authService } from '../../features/auth/auth.service';
import { navigate } from '../../infrastructure/router';
import { storageAdapter } from '../../infrastructure/storage-adapter';
import { decodeTokenPayload, resolveRoleFromPayload } from '../../features/auth/auth.service';
import { ssoService } from '../../features/auth/sso.service';

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const hamburgerIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>`;

const customersIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;

const timelineIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><circle cx="12" cy="6" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="18" r="2"/><line x1="14" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="10" y2="12"/><line x1="14" y1="18" x2="20" y2="18"/></svg>`;

const timelineAdminIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><circle cx="12" cy="6" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="18" r="2"/><path d="M20 14l2 2-5 5-3-3 2-2 1 1z"/></svg>`;

const helpAdminIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;

const feedbackIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

const statisticsIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>`;

const settingsIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z"/></svg>`;

const passwordIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;

const logoutIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let dropdownOpen = false;
let closeDropdownOnOutsideClick: ((e: MouseEvent) => void) | null = null;

// ---------------------------------------------------------------------------
// Role check helper
// ---------------------------------------------------------------------------

function isEmployeeOrAdmin(): boolean {
  const token = storageAdapter.getAccessToken();
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  const role = resolveRoleFromPayload(payload);
  return role === 'employee' || role === 'admin';
}

function isSsoUser(): boolean {
  return sessionStorage.getItem('snaphub_login_method') === 'sso';
}

function isAdminUser(): boolean {
  const token = storageAdapter.getAccessToken();
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  return resolveRoleFromPayload(payload) === 'admin';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render the page header into the given container element.
 * The header is inserted at the top of the main content area.
 * On mobile, a hamburger button is shown inside the header.
 * On the right side, a user menu or login link is shown.
 */
export function renderPageHeader(container: HTMLElement): void {
  // Remove existing header if present (re-render scenario)
  const existing = container.querySelector('.page-header');
  if (existing) {
    existing.remove();
  }

  const title = getTitle();
  const authenticated = isAuthenticated();

  const header = document.createElement('header');
  header.className = 'page-header';
  header.innerHTML = `
    <div class="page-header__left">
      <button
        class="page-header__hamburger btn-icon"
        type="button"
        aria-label="Menü öffnen"
        aria-expanded="false"
      >
        ${hamburgerIcon}
      </button>
      <h2 class="page-header__title">${escapeHtml(title)}</h2>
    </div>
    <div class="page-header__right">
      ${authenticated ? renderUserMenuButton() : renderLoginLink()}
    </div>
  `;

  // Prepend header to container
  container.insertBefore(header, container.firstChild);

  // Attach hamburger click handler
  const hamburgerBtn = header.querySelector<HTMLButtonElement>('.page-header__hamburger');
  if (hamburgerBtn) {
    hamburgerBtn.addEventListener('click', () => {
      toggleMobileSidebar();
      const sidebarEl = document.getElementById('sidebar');
      const isOpen = sidebarEl?.classList.contains('sidebar--open') ?? false;
      hamburgerBtn.setAttribute('aria-expanded', String(isOpen));
      hamburgerBtn.setAttribute('aria-label', isOpen ? 'Menü schließen' : 'Menü öffnen');
    });
  }

  // Attach user menu handlers (if authenticated)
  if (authenticated) {
    attachUserMenuHandlers(header);
  }
}

// ---------------------------------------------------------------------------
// User Menu rendering
// ---------------------------------------------------------------------------

function renderUserMenuButton(): string {
  const user = authService.getUser();
  const initials = getUserInitials(user?.email);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Benutzer';

  return `
    <div class="user-menu" data-user-menu>
      <button
        class="user-menu__trigger"
        type="button"
        title="${escapeHtml(displayName)}"
        aria-label="Benutzermenü"
        aria-expanded="false"
        aria-haspopup="true"
      >
        <span class="user-menu__avatar">${escapeHtml(initials)}</span>
      </button>
      <div class="user-menu__dropdown" role="menu" aria-label="Benutzermenü" hidden>
        ${isEmployeeOrAdmin() ? `
        <a href="#/customers" class="user-menu__item" role="menuitem" data-route="/customers">
          <span class="user-menu__item-icon">${customersIcon}</span>
          <span>Meine Kunden</span>
        </a>
        ` : ''}
        <a href="#/timeline" class="user-menu__item" role="menuitem" data-route="/timeline">
          <span class="user-menu__item-icon">${timelineIcon}</span>
          <span>Timeline</span>
        </a>
        <a href="#/help" class="user-menu__item" role="menuitem" data-route="/help">
          <span class="user-menu__item-icon">${helpAdminIcon}</span>
          <span>Hilfe</span>
        </a>
        ${isEmployeeOrAdmin() ? `
        <div class="user-menu__divider"></div>
        <a href="#${isAdminUser() ? '/admin/feedback' : '/feedback'}" class="user-menu__item" role="menuitem" data-route="${isAdminUser() ? '/admin/feedback' : '/feedback'}">
          <span class="user-menu__item-icon">${feedbackIcon}</span>
          <span>Feedback</span>
        </a>
        ` : ''}
        ${isEmployeeOrAdmin() ? `
        <a href="#/admin/statistics" class="user-menu__item" role="menuitem" data-route="/admin/statistics">
          <span class="user-menu__item-icon">${statisticsIcon}</span>
          <span>Statistik-Dashboard</span>
        </a>
        ` : ''}
        ${isAdminUser() ? `
        <a href="#/admin/feature-timeline" class="user-menu__item" role="menuitem" data-route="/admin/feature-timeline">
          <span class="user-menu__item-icon">${timelineAdminIcon}</span>
          <span>Timeline verwalten</span>
        </a>
        ` : ''}
        ${isAdminUser() ? `
        <a href="#/admin/help" class="user-menu__item" role="menuitem" data-route="/admin/help">
          <span class="user-menu__item-icon">${helpAdminIcon}</span>
          <span>Hilfe verwalten</span>
        </a>
        ` : ''}
        <div class="user-menu__divider"></div>
        <a href="#/settings" class="user-menu__item" role="menuitem" data-route="/settings">
          <span class="user-menu__item-icon">${settingsIcon}</span>
          <span>Einstellungen</span>
        </a>
        ${isSsoUser() ? `
        <a href="javascript:void(0)" class="user-menu__item" role="menuitem" data-action="change-password-sso">
          <span class="user-menu__item-icon">${passwordIcon}</span>
          <span>Passwort ändern</span>
        </a>
        ` : `
        <a href="#/change-password" class="user-menu__item" role="menuitem" data-route="/change-password">
          <span class="user-menu__item-icon">${passwordIcon}</span>
          <span>Passwort ändern</span>
        </a>
        `}
        <div class="user-menu__divider"></div>
        <a href="javascript:void(0)" class="user-menu__item" role="menuitem" data-action="logout" title="Nur aus der SnapHub-App abmelden. Die Meesenburg ID-Sitzung bleibt aktiv.">
          <span class="user-menu__item-icon">${logoutIcon}</span>
          <span>Aus App abmelden</span>
        </a>
        ${isSsoUser() ? `
        <a href="javascript:void(0)" class="user-menu__item user-menu__item--danger" role="menuitem" data-action="sso-logout" title="Komplett aus der Meesenburg ID abmelden. Danach ist eine erneute Anmeldung bei Meesenburg ID erforderlich.">
          <span class="user-menu__item-icon">${logoutIcon}</span>
          <span>Aus Meesenburg ID abmelden</span>
        </a>
        ` : ''}
      </div>
    </div>
  `;
}

function renderLoginLink(): string {
  return `<a href="#/login" class="user-menu__login-link">Anmelden</a>`;
}

// ---------------------------------------------------------------------------
// User Menu interaction
// ---------------------------------------------------------------------------

function attachUserMenuHandlers(header: HTMLElement): void {
  const trigger = header.querySelector<HTMLButtonElement>('.user-menu__trigger');
  const dropdown = header.querySelector<HTMLElement>('.user-menu__dropdown');
  if (!trigger || !dropdown) return;

  // Toggle dropdown on trigger click
  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleDropdown(trigger, dropdown);
  });

  // Handle menu item clicks
  dropdown.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-route], [data-action]');
    if (!target) return;

    e.preventDefault();
    closeDropdown(trigger, dropdown);

    const route = target.dataset.route;
    if (route) {
      navigate(route);
      return;
    }

    const action = target.dataset.action;
    if (action === 'logout') {
      void authService.logoutAsync().then(() => {
        navigate('/login');
      });
    }

    if (action === 'sso-logout') {
      void authService.logoutAsync().then(() => {
        // Terminate Keycloak session so a different SSO user can log in
        window.location.href = ssoService.getLogoutUrl();
      });
    }

    if (action === 'change-password-sso') {
      void ssoService.initiatePasswordChange();
    }
  });

  // Keyboard support: Escape closes dropdown
  dropdown.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDropdown(trigger, dropdown);
      trigger.focus();
    }
  });
}

function toggleDropdown(trigger: HTMLButtonElement, dropdown: HTMLElement): void {
  dropdownOpen = !dropdownOpen;

  if (dropdownOpen) {
    dropdown.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    // Close on outside click
    closeDropdownOnOutsideClick = (e: MouseEvent) => {
      const userMenu = trigger.closest('[data-user-menu]');
      if (userMenu && !userMenu.contains(e.target as Node)) {
        closeDropdown(trigger, dropdown);
      }
    };
    // Delay to avoid catching the current click
    setTimeout(() => {
      document.addEventListener('click', closeDropdownOnOutsideClick!);
    }, 0);
  } else {
    closeDropdown(trigger, dropdown);
  }
}

function closeDropdown(trigger: HTMLButtonElement, dropdown: HTMLElement): void {
  dropdownOpen = false;
  dropdown.hidden = true;
  trigger.setAttribute('aria-expanded', 'false');

  if (closeDropdownOnOutsideClick) {
    document.removeEventListener('click', closeDropdownOnOutsideClick);
    closeDropdownOnOutsideClick = null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getUserInitials(email?: string | null): string {
  if (!email) return '?';
  // Use first letter of email or name
  const name = email.split('@')[0];
  if (!name) return '?';
  // If name has a dot (first.last), use first letters of each part
  const parts = name.split('.');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
